import React, { useEffect, useRef } from 'react';
import ApexCharts from 'apexcharts';
import { Box, Paper, Typography } from '@mui/material';
import { BarChart } from '@mui/icons-material';

/**
 * 活動分析圖表組件 - 展示節點累計圖、時間分布圖和成員活躍度圖
 * 
 * @param {Array} nodes - 節點數據陣列
 * @param {boolean} visible - 是否顯示圖表
 */
const ActivityAnalysisCharts = ({ nodes = [], visible = true }) => {
  const cumulativeChartRef = useRef(null);
  const timeDistChartRef = useRef(null);
  const userActivityChartRef = useRef(null);
  const chartsRef = useRef({});

  useEffect(() => {
    if (!visible || nodes.length === 0) {
      return;
    }

    // 延遲渲染以確保 DOM 已更新
    const timer = setTimeout(() => {
      renderAllCharts();
    }, 100);

    return () => {
      clearTimeout(timer);
      destroyAllCharts();
    };
  }, [nodes, visible]);

  const destroyAllCharts = () => {
    Object.values(chartsRef.current).forEach(chart => {
      if (chart) {
        try {
          chart.destroy();
        } catch (e) {
          console.error('Error destroying chart:', e);
        }
      }
    });
    chartsRef.current = {};
  };

  const renderAllCharts = () => {
    destroyAllCharts();
    renderCumulativeChart();
    renderTimeDistributionChart();
    renderUserActivityChart();
  };

  // 準備累計數據
  const prepareData = (nodes) => {
    const sortedNodes = [...nodes].sort((a, b) => new Date(a.updatedAt) - new Date(b.updatedAt));
    const authorCumulativeCount = {};
    const seriesData = {};

    sortedNodes.forEach(node => {
      const { author, updatedAt } = node;
      if (!author) return;
      
      if (!authorCumulativeCount[author]) {
        authorCumulativeCount[author] = 0;
        seriesData[author] = [];
      }
      authorCumulativeCount[author]++;
      seriesData[author].push({
        x: new Date(updatedAt),
        y: authorCumulativeCount[author]
      });
    });

    return seriesData;
  };

  const prepareSeries = (seriesData) => {
    return Object.keys(seriesData).map(author => ({
      name: author || 'Unknown Author',
      data: seriesData[author]
    }));
  };

  // 渲染累計節點圖
  const renderCumulativeChart = () => {
    if (!cumulativeChartRef.current) return;

    const series = prepareSeries(prepareData(nodes));
    
    const options = {
      chart: {
        type: 'line',
        height: 350,
        animations: {
          enabled: true
        }
      },
      series: series,
      xaxis: {
        type: 'datetime',
        title: {
          text: '時間'
        }
      },
      yaxis: {
        title: {
          text: '累計節點數'
        }
      },
      title: {
        text: '累計節點時間圖',
        align: 'center',
        style: {
          fontSize: '18px',
          fontWeight: 600,
          color: '#333'
        }
      },
      stroke: {
        curve: 'smooth',
        width: 2
      },
      tooltip: {
        x: {
          format: 'yyyy/MM/dd HH:mm'
        }
      }
    };

    try {
      const chart = new ApexCharts(cumulativeChartRef.current, options);
      chart.render();
      chartsRef.current.cumulative = chart;
    } catch (e) {
      console.error('Error rendering cumulative chart:', e);
    }
  };

  // 分析小組活動時間分布
  const analyzeTimeDistribution = (nodes) => {
    const timeDistribution = {};
    for (let i = 0; i < 24; i++) {
      timeDistribution[i] = 0;
    }

    nodes.forEach(node => {
      if (node.createdAt || node.updatedAt) {
        const date = new Date(node.createdAt || node.updatedAt);
        const hour = date.getHours();
        timeDistribution[hour] = (timeDistribution[hour] || 0) + 1;
      }
    });

    return timeDistribution;
  };

  // 渲染時間分布圖
  const renderTimeDistributionChart = () => {
    if (!timeDistChartRef.current) return;

    const timeDistribution = analyzeTimeDistribution(nodes);
    const hourLabels = Array.from({ length: 24 }, (_, i) => i + ':00');
    const timeData = Object.keys(timeDistribution)
      .sort((a, b) => parseInt(a) - parseInt(b))
      .map(hour => timeDistribution[hour] || 0);

    const options = {
      chart: {
        type: 'area',
        height: 350
      },
      dataLabels: {
        enabled: false
      },
      stroke: {
        curve: 'smooth',
        width: 2
      },
      xaxis: {
        categories: hourLabels,
        title: {
          text: '時間（小時）'
        },
        labels: {
          rotate: -45,
          rotateAlways: false,
          hideOverlappingLabels: true
        }
      },
      yaxis: {
        title: {
          text: '節點數量'
        }
      },
      fill: {
        type: 'gradient',
        gradient: {
          shadeIntensity: 1,
          opacityFrom: 0.7,
          opacityTo: 0.3,
          stops: [0, 90, 100]
        }
      },
      series: [{
        name: '節點數量',
        data: timeData
      }],
      title: {
        text: '小組活動時間分布',
        align: 'center',
        style: {
          fontSize: '18px',
          fontWeight: 600,
          color: '#333'
        }
      },
      tooltip: {
        y: {
          formatter: function (val) {
            return val + " 個節點";
          }
        }
      },
      colors: ['#4F46E5']
    };

    try {
      const chart = new ApexCharts(timeDistChartRef.current, options);
      chart.render();
      chartsRef.current.timeDist = chart;
    } catch (e) {
      console.error('Error rendering time distribution chart:', e);
    }
  };

  // 分析用戶活躍度
  const analyzeUserActivity = (nodes) => {
    const userActivity = {};
    nodes.forEach(node => {
      if (node.author) {
        userActivity[node.author] = (userActivity[node.author] || 0) + 1;
      }
    });
    return userActivity;
  };

  // 渲染用戶活躍度圖
  const renderUserActivityChart = () => {
    if (!userActivityChartRef.current) return;

    const userActivity = analyzeUserActivity(nodes);
    const sortedUsers = Object.entries(userActivity).sort((a, b) => b[1] - a[1]);
    const userNames = sortedUsers.map(([name]) => name);
    const userCounts = sortedUsers.map(([, count]) => count);
    const totalNodes = nodes.length;
    const percentages = userCounts.map(count => 
      totalNodes > 0 ? ((count / totalNodes) * 100).toFixed(1) : 0
    );

    const options = {
      chart: {
        type: 'bar',
        height: 350
      },
      plotOptions: {
        bar: {
          horizontal: true,
          barHeight: '70%',
          dataLabels: {
            position: 'right'
          }
        }
      },
      dataLabels: {
        enabled: true,
        formatter: function (val, opts) {
          const index = opts.dataPointIndex;
          return userCounts[index] + ' 個 (' + percentages[index] + '%)';
        },
        style: {
          fontSize: '12px',
          colors: ["#304758"]
        }
      },
      xaxis: {
        categories: userNames,
        title: {
          text: '節點數量'
        }
      },
      yaxis: {
        title: {
          text: '用戶'
        }
      },
      fill: {
        opacity: 1
      },
      series: [{
        name: '節點數量',
        data: userCounts
      }],
      title: {
        text: '成員活躍度對比',
        align: 'center',
        style: {
          fontSize: '18px',
          fontWeight: 600,
          color: '#333'
        }
      },
      tooltip: {
        y: {
          formatter: function (val, { dataPointIndex }) {
            return val + " 個節點 (" + percentages[dataPointIndex] + "%)";
          }
        }
      },
      colors: ['#10B981']
    };

    try {
      const chart = new ApexCharts(userActivityChartRef.current, options);
      chart.render();
      chartsRef.current.userActivity = chart;
    } catch (e) {
      console.error('Error rendering user activity chart:', e);
    }
  };

  if (!visible) {
    return null;
  }

  if (nodes.length === 0) {
    return (
      <Paper 
        elevation={2}
        sx={{ 
          padding: 4,
          textAlign: 'center',
          backgroundColor: '#f8f9fa',
          borderRadius: 2,
          border: '2px dashed #dee2e6',
        }}
      >
        <BarChart sx={{ fontSize: 64, color: '#6c757d', mb: 2 }} />
        <Typography variant="h5" sx={{ fontWeight: 500, color: '#495057', mb: 2 }}>
          目前沒有學習數據
        </Typography>
        <Typography variant="body1" sx={{ color: '#6c757d' }}>
          該小組尚未開始任何學習活動
        </Typography>
      </Paper>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* 累計節點圖 */}
      <Paper elevation={2} sx={{ p: 2 }}>
        <Box ref={cumulativeChartRef} sx={{ width: '100%', height: 350 }} />
      </Paper>

      {/* 時間分布和用戶活躍度並排 */}
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Paper elevation={2} sx={{ p: 2, flex: 1 }}>
          <Box ref={timeDistChartRef} sx={{ width: '100%', height: 350 }} />
        </Paper>
        <Paper elevation={2} sx={{ p: 2, flex: 1 }}>
          <Box ref={userActivityChartRef} sx={{ width: '100%', height: 350 }} />
        </Paper>
      </Box>
    </Box>
  );
};

export default ActivityAnalysisCharts;
